import { useEffect, useMemo, useRef } from "react";
import type { Message } from "@ag-ui/client";
import { HelpCircle, Trash2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { useCurrentPageContext } from "~/contexts/page-context";
import { useFetcher } from "~/hooks/useFetcher";
import { cn } from "~/lib/utils";
import { ReplayHttpAgent } from "~/pages/manifold-nextgen/components/conversations/ReplayHttpAgent";
import { pendingInputOf } from "~/pages/manifold-nextgen/components/conversations/awaitingInput";
import { useAgent } from "~/pages/manifold-nextgen/components/conversations/useAgent";
import { useRunError } from "~/pages/manifold-nextgen/components/conversations/useRunError";
import { useRunReconcile } from "~/pages/manifold-nextgen/components/conversations/useRunReconcile";
import { useTurns } from "~/pages/manifold-nextgen/components/conversations/useTurns";
import { type Agent, agentPath } from "~/services/agents";
import { conversationAguiUrl } from "~/services/conversations";
import { isApiError } from "~/utils/apiClient";
import { PlatformComposer } from "./PlatformComposer";
import { PlatformContextTooltip } from "./PlatformContextTooltip";
import { PlatformTurns } from "./PlatformTurns";
import { usePlatformSession, type PlatformSessionState } from "./usePlatformSession";

// The backend resolves the real agent from the conversation's app.
const AGENT_NAME = "default";

const MAX_SUGGESTIONS = 3;

// One per starter prompt, at ragged widths.
const SUGGESTION_SKELETON_WIDTHS = ["w-44", "w-56", "w-48"];

const SuggestionSkeletons = () =>
  SUGGESTION_SKELETON_WIDTHS.map((width) => (
    <Skeleton key={width} className={cn("h-8 rounded-full", width)} />
  ));

export function PlatformChatPanel() {
  const { session, error, clearChat } = usePlatformSession();

  if (error) {
    // A 404 means ENABLE_PLATFORM_AGENT is off here, not a fault.
    const gatedOff = isApiError(error) && error.status === 404;
    return (
      <PanelShell>
        <p className="p-4 text-[13px] text-muted-foreground">
          {gatedOff
            ? "Help isn't available in this environment yet."
            : "Couldn't open a session. Close this and try again."}
        </p>
      </PanelShell>
    );
  }

  if (!session) {
    return (
      <PanelShell>
        {/* Shaped like the empty session, so the panel doesn't reflow. */}
        <div
          className="flex flex-1 flex-col items-start justify-end gap-2 p-3"
          aria-busy="true"
          aria-label="Opening help session"
        >
          <SuggestionSkeletons />
        </div>
        <div className="border-t p-3">
          <Skeleton className="h-11 w-full rounded-2xl" />
        </div>
      </PanelShell>
    );
  }

  // Remount per conversation so a cleared session's messages don't carry over.
  return <PlatformChat key={session.conversationId} session={session} onClearChat={clearChat} />;
}

interface PlatformChatProps {
  session: NonNullable<PlatformSessionState["session"]>;
  onClearChat: () => void;
}

const PlatformChat = ({ session, onClearChat }: PlatformChatProps) => {
  const { conversationId, connect } = session;
  const scrollRef = useRef<HTMLDivElement>(null);

  const rawAgent = useMemo(
    () =>
      new ReplayHttpAgent({
        agentId: AGENT_NAME,
        threadId: conversationId,
        url: conversationAguiUrl(conversationId),
        headers: { Accept: "text/event-stream" },
        initialMessages: connect.messages,
        initialState: connect.state,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snapshot at mount
    []
  );
  const { agent } = useAgent(rawAgent);
  const runError = useRunError(agent);
  const turns = useTurns(agent.messages, agent.isRunning, undefined, runError);
  const { reconciling, cancel, supersedeCancel } = useRunReconcile(
    rawAgent,
    conversationId,
    turns,
    connect.status === "MESSAGE_IN_PROGRESS",
    pendingInputOf(connect)
  );
  const isEmpty = turns.length === 0;

  // Pin to the newest content as it streams.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns]);

  // Read at send time: the panel stays open across navigation.
  const page = useCurrentPageContext();

  const ask = (text: string) => {
    supersedeCancel();
    agent.addMessage({ id: crypto.randomUUID(), role: "user", content: text } as Message);
    // A failed run surfaces through the reconcile; this only keeps the reason.
    agent
      .runAgent(page ? { forwardedProps: { page } } : undefined)
      .catch((err) => console.error("Help panel run failed", err));
  };

  // `/connect` carries only an agent summary; starter prompts need the full record.
  const { data: fullAgent, isLoading: loadingPrompts } = useFetcher<Agent>(
    connect.agent ? agentPath(connect.agent.id) : null
  );
  const suggestions = (fullAgent?.starter_prompts ?? [])
    .slice()
    .sort((a, b) => a.position - b.position)
    .slice(0, MAX_SUGGESTIONS);

  return (
    <PanelShell canClear={!isEmpty} onClearChat={onClearChat}>
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        {isEmpty ? (
          <div
            className="flex h-full flex-col items-start justify-end gap-2 p-3"
            aria-busy={loadingPrompts || undefined}
            aria-label={loadingPrompts ? "Loading starter prompts" : undefined}
          >
            {loadingPrompts ? (
              <SuggestionSkeletons />
            ) : (
              suggestions.map((prompt) => (
                <Button
                  key={prompt.id}
                  variant="outline"
                  size="sm"
                  onClick={() => ask(prompt.text)}
                  className="h-auto rounded-full py-1.5 text-left text-[13px] font-normal whitespace-normal text-muted-foreground"
                >
                  {prompt.text}
                </Button>
              ))
            )}
          </div>
        ) : (
          <ol className="flex flex-col gap-3 p-3" role="log">
            <PlatformTurns turns={turns} />
          </ol>
        )}
      </div>
      <div className="border-t p-3">
        <PlatformComposer
          isRunning={agent.isRunning}
          disabled={reconciling}
          onSubmit={ask}
          onCancel={() => void cancel()}
        />
      </div>
    </PanelShell>
  );
};

// The panel's chrome, shared by the loading, error and chat states.
const PanelShell = ({
  canClear = false,
  onClearChat,
  children,
}: {
  canClear?: boolean;
  onClearChat?: () => void;
  children: React.ReactNode;
}) => (
  <div className="flex h-[28rem] flex-col">
    <div className="flex shrink-0 items-center justify-between border-b px-3 py-2">
      <div className="flex items-center gap-1.5">
        <span className="text-sm font-medium">Ask AI</span>
        <Tooltip>
          <TooltipTrigger
            aria-label="What this assistant does"
            className="cursor-help text-muted-foreground hover:text-foreground"
          >
            <HelpCircle className="size-3.5" aria-hidden />
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[15rem]">
            Answers questions about how Manifold works and helps you find your projects and
            datasets.
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="flex items-center gap-1">
        <PlatformContextTooltip />
        <Tooltip>
          <TooltipTrigger asChild>
            {/* Wrapped: a disabled button fires no pointer events for the tooltip. */}
            <span>
              <Button
                variant="ghost"
                size="icon"
                disabled={!canClear}
                onClick={onClearChat}
                aria-label="Clear chat"
                className="size-7 text-muted-foreground hover:text-foreground"
              >
                <Trash2 className="size-4" />
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent side="left">Clear chat</TooltipContent>
        </Tooltip>
      </div>
    </div>
    {children}
  </div>
);
